<script lang="ts" setup>
import CategoriesField from '@/components/categories/CategoriesField.vue';
import CategoryField from '@/components/categories/CategoryField.vue';
import {
  impactAreaItems,
  Initiative,
  InitiativeStatus,
} from '@/models/initiative.model';
import { ref, PropType, watch } from 'vue';
import RichTextField from '@/components/common/RichTextField.vue';
import MediaList from '@/components/media/MediaList.vue';
import { MediaPurpose } from '@/models/media.model';
import AddressField from '@/components/common/AddressField.vue';
import ContactField from '@/components/common/ContactField.vue';
import { Contact } from '@/models/contact.model';

const props = defineProps({
  modelValue: { type: Object as PropType<Initiative>, required: true },
});
const value = ref<Initiative>({ ...(props.modelValue || {}) });

const emit = defineEmits([
  'update:modelValue',
  'update:published',
  'remove-contact',
  'update:status',
]);

const update = () => {
  emit('update:modelValue', value.value);
};
watch(
  () => props.modelValue,
  () => {
    Object.assign(value.value, props.modelValue);
  },
  { deep: true },
);

const togglePublished = async (published: boolean) => {
  if (value.value.id !== -1) {
    emit('update:published', { ...value.value, published: !published });
  }
  value.value.published = !published;
};

const removeContact = (item: Contact) => {
  emit('remove-contact', item);
};

const openURL = () => {
  window.open(`/#/initiative/${value.value.publicUUID}/edit`, 'new');
};

const setPublished = async () => {
  emit('update:status', { ...value.value, status: InitiativeStatus.Published });
};
const setReadyForReview = async () => {
  emit('update:status', {
    ...value.value,
    status: InitiativeStatus.ReadyForReview,
  });
};

function checkUrl(url: string) {
  window.open(url, '_new');
}
</script>
<template>
  <v-alert
    :color="value.published ? 'success' : 'warning'"
    class="ma-4 pa-4"
    v-if="value.createdByUser && value.status === InitiativeStatus.Published"
  >
    This initiative was created by content editor
    <strong
      >{{ value.createdByUser?.firstName }}
      {{ value.createdByUser?.lastName }}</strong
    >.
    <i v-if="!value.published">This initiative still needs to be published</i>
  </v-alert>
  <v-alert
    color="warning"
    class="ma-4 pa-4"
    v-if="
      value.createdByUser &&
      value.status === InitiativeStatus.ReadyForPublishing
    "
  >
    This initiative was created by content editor
    <strong
      >{{ value.createdByUser?.firstName }}
      {{ value.createdByUser?.lastName }}</strong
    >. Check the initiative and set it to <em>"published"</em> or return it to
    the user with status <em>"ready for review"</em>.
  </v-alert>
  <v-alert
    color="warning"
    class="ma-8 pa-8"
    v-if="
      value.createdByUser &&
      [InitiativeStatus.Editing, InitiativeStatus.ReadyForReview].includes(
        value.status,
      )
    "
  >
    This initiative was created by content editor
    <strong
      >{{ value.createdByUser?.firstName }}
      {{ value.createdByUser?.lastName }}</strong
    >. Please wait until it has status <em>"Shift Review"</em> before doing
    anything with it.
  </v-alert>

  <v-btn
    @click="openURL"
    color="info"
    v-if="value.publicUUID"
    style="max-width: 200px; position: absolute; top: 20px; right: 60px"
    >Open Public URL</v-btn
  >

  <v-container
    v-if="[InitiativeStatus.ReadyForPublishing].includes(value.status)"
  >
    <v-row>
      <v-col>
        <v-btn
          class=""
          @click="setReadyForReview"
          color="warning"
          block
          v-if="value.status === InitiativeStatus.ReadyForPublishing"
          >Ready for Review</v-btn
        >
      </v-col>
      <v-col>
        <v-btn
          @click="setPublished"
          color="success"
          block
          v-if="value.status === InitiativeStatus.ReadyForPublishing"
          >Published</v-btn
        >
      </v-col>
    </v-row>
  </v-container>

  <v-timeline
    v-if="value.createdByUser && value.status !== InitiativeStatus.Published"
    class="ma-16"
    direction="horizontal"
    density="compact"
    :fill-dot="false"
    size="x-small"
    align="start"
  >
    <v-timeline-item
      :dot-color="
        value.status === InitiativeStatus.Editing ? 'primary' : '#efefef'
      "
    >
      <v-chip
        variant="flat"
        density="compact"
        :color="
          value.status === InitiativeStatus.Editing ? 'primary' : '#808080'
        "
        >{{ $t('initiative.edit.status_editing') }}</v-chip
      >
    </v-timeline-item>
    <v-timeline-item
      :dot-color="
        value.status === InitiativeStatus.ReadyForReview ? 'primary' : '#efefef'
      "
    >
      <v-chip
        variant="flat"
        density="compact"
        :color="
          value.status === InitiativeStatus.ReadyForReview
            ? 'primary'
            : '#808080'
        "
        >{{ $t('initiative.edit.status_review_long') }}</v-chip
      >
    </v-timeline-item>
    <v-timeline-item
      :dot-color="
        value.status === InitiativeStatus.ReadyForPublishing
          ? 'primary'
          : '#efefef'
      "
    >
      <v-chip
        variant="flat"
        density="compact"
        :color="
          value.status === InitiativeStatus.ReadyForPublishing
            ? 'primary'
            : '#808080'
        "
        >{{ $t('initiative.edit.status_publishing_long') }}</v-chip
      >
    </v-timeline-item>
    <v-timeline-item
      :dot-color="
        value.status === InitiativeStatus.Published ? 'primary' : '#efefef'
      "
    >
      <v-chip
        variant="flat"
        density="compact"
        :color="
          value.status === InitiativeStatus.Published ? 'primary' : '#808080'
        "
        >{{ $t('initiative.edit.status_published') }}</v-chip
      >
    </v-timeline-item>
  </v-timeline>

  <v-row>
    <v-col cols="9">
      <v-text-field
        v-model="value.title"
        @change="update"
        label="Title"
        required
        hide-details
      ></v-text-field>
    </v-col>
    <v-col class="pt-6">
      <v-btn
        block
        :disabled="value.status !== InitiativeStatus.Published"
        @click="() => togglePublished(value.published)"
        :color="!value.published ? 'success' : 'warning'"
        >{{ value.published ? 'Un-publish' : 'Publish' }}</v-btn
      >
    </v-col>
  </v-row>
  <v-row>
    <v-col>
      <v-text-field
        v-model="value.quizTitle"
        @change="update"
        label="Quiz Title"
        required
        hide-details
        counter
      ></v-text-field>
    </v-col>
  </v-row>
  <v-row>
    <v-col cols="9">
      <v-text-field
        v-model="value.website"
        @change="update"
        placeholder="https://"
        label="Website URL"
        required
      ></v-text-field>
    </v-col>
    <v-col class="pt-6">
      <v-btn
        block
        color="info"
        @click="checkUrl(value.website)"
        :disabled="value.website == ''"
        >Check</v-btn
      >
    </v-col>
  </v-row>
  <v-row dense>
    <v-col>
      <v-checkbox
        label="Recommended"
        density="compact"
        v-model="value.recommended"
        @change="update"
      />
    </v-col>
    <v-col cols="3">
      <v-text-field
        v-model="value.favourite"
        label="Favourite count"
        density="compact"
        @change="update"
      />
    </v-col>
  </v-row>

  <AddressField v-model="value.address" @change="update" />

  <ContactField
    v-model="value.contacts"
    v-if="value.contacts"
    @change="update"
    @remove="removeContact"
  />

  <v-select
    v-model="value.impactArea"
    :items="impactAreaItems"
    @update:model-value="update"
    label="Area"
    required
  ></v-select>

  <CategoryField
    main
    v-model="value.mainCategory"
    @change="update"
    disabled
    label="Main Category"
  />

  <CategoriesField
    v-model="value.categories"
    @change="update"
    v-if="value.categories"
  />

  <RichTextField
    label="Short description"
    :height="200"
    v-model="value.shortDescription"
    hint="Used in stories when users choose their interest"
    @change="update"
    :media="modelValue.media"
  />
  <RichTextField
    label="Description"
    v-model="value.description"
    hint="Used as the full text of the action"
    @change="update"
    :media="modelValue.media"
  />

  <MediaList
    v-model="value"
    @change="update"
    :required="[MediaPurpose.BackgroundImage, MediaPurpose.Main]"
  />
</template>
<style scoped>
.slider-number {
  width: 40px;
  text-align: center;
}
</style>
